import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';
import StyleIcon from '@mui/icons-material/Style';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import WorkIcon from '@mui/icons-material/Work';
import {
  Avatar,
  ClickAwayListener,
  Grid,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { BsPersonCheck } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import { CrmJobDTO } from 'wisestep-api';
import {
  Button,
  ChipsType,
  WsChip,
  WsChipVariants,
  WsColors,
  WsTagsList,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
} from 'wisestep-base';

import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';

interface ICrmJobDialog {
  openJobInfoDialog?: boolean;
  onClose?: (click: boolean) => void;
  jobInfo: CrmJobDTO;
}

const CrmJobDialog = ({ openJobInfoDialog = false, jobInfo }: ICrmJobDialog) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>();
  const [open, setOpen] = useState(openJobInfoDialog);
  const navigate = useNavigate();

  const handleClose = () => setOpen(false);

  const viewJobInfo = () => setOpen(true);

  const handlePopoverOpen = (event) => setAnchorEl(event?.currentTarget);

  const processedData = [
    {
      key: 'Job Url',
      value: jobInfo?.jobApplyUrl ? 'View Job' : 'NA',
      icon: <InsertLinkOutlinedIcon sx={{ color: WsColors.GreyBlack }} />,
      clickable: !!jobInfo?.jobApplyUrl,
      link: true,
      hover: false,
      url: jobInfo?.jobApplyUrl,
      onClick: async () => {
        if (jobInfo?.jobApplyUrl) {
          const url = jobInfo.jobApplyUrl.startsWith('http')
            ? jobInfo.jobApplyUrl
            : `https://${jobInfo.jobApplyUrl}`;
          openUrlInNewTab(url);
        }
      },
    },
    {
      key: 'Source',
      value: jobInfo?.jobSource ?? 'NA',
      icon: <TravelExploreIcon sx={{ color: WsColors.GreyBlack }} />,
      clickable: false,
      link: false,
      hover: false,
    },
    {
      key: 'Created by',
      value: jobInfo?.createdByName ?? 'NA',
      icon: <BsPersonCheck size={24} color={WsColors.GreyBlack} />,
      clickable: true,
      link: true,
      hover: false,
      url: `/users/details-page?email=${jobInfo.createdBy}&isLink=true&isUserAnalytics=true`,
      onClick: async () => {
        navigate(`/users/details-page?email=${jobInfo.createdBy}&isLink=true&isUserAnalytics=true`);
      },
    },
  ];

  const handleJobClick = (event: React.MouseEvent) => {
    viewJobInfo();
    handlePopoverOpen(event);
  };

  return (
    <Grid item>
      <ClickAwayListener onClickAway={handleClose}>
        <div>
          <IconButton
            onClick={handleJobClick}
            sx={{ color: WsColors.TheBlues400 }}
            aria-describedby={open ? 'job-popover' : undefined}
          >
            <InfoIcon />
          </IconButton>
          <Popover
            id={open ? 'job-popover' : undefined}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              style: {
                width: '800px',
                borderRadius: '10px',
              },
            }}
          >
            <Grid container direction="row" alignItems="center" sx={{ p: 2, pb: 1 }}>
              <Grid item>
                <Grid display="flex" alignItems="center" sx={{ cursor: 'pointer' }}>
                  <Avatar sx={{ mr: 2, bgcolor: WsColors.TheBlues400 }}>
                    <WorkIcon />
                  </Avatar>
                  <WsTypography
                    cursor="pointer"
                    variant={WsTypoVariants.SUB_TITLE}
                    color={WsTypoColors.PRIMARY600}
                  >
                    {jobInfo?.title ?? 'NA'}
                  </WsTypography>
                </Grid>
              </Grid>
              {jobInfo.linkedInJobUrl && (
                <Grid item>
                  <Tooltip title="Job Url">
                    <IconButton
                      onClick={() => {
                        const url = jobInfo.linkedInJobUrl?.startsWith('http')
                          ? jobInfo.linkedInJobUrl
                          : `https://${jobInfo.linkedInJobUrl}`;
                        openUrlInNewTab(url);
                      }}
                    >
                      <InsertLinkOutlinedIcon color={WsTypoColors.SECONDARY} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
              <Grid item ml={1}>
                <WsChip value={jobInfo.jobStatus} variant={WsChipVariants.PRIMARY} />
              </Grid>
              <Grid item sx={{ ml: 'auto' }}>
                <Button onClick={handleClose}>
                  <CloseIcon sx={{ color: WsColors.GenericRichBlack, fontWeight: 'bold' }} />
                </Button>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container p={2}>
                {processedData.map((data, index) => (
                  <Grid item container xs={6} key={index} pt={1}>
                    <Grid item xs={1}>
                      {data.icon}
                    </Grid>
                    <Grid item xs={3}>
                      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.TERTIARY900}>
                        {data.key}:
                      </WsTypography>
                    </Grid>
                    <Grid item xs={8}>
                      {data.link && data.clickable ? (
                        <WsTypography
                          variant={WsTypoVariants.BODY1}
                          color={WsTypoColors.PRIMARY600}
                          onClick={() => data.onClick?.()}
                          cursor="pointer"
                        >
                          {data.value}
                        </WsTypography>
                      ) : (
                        <WsTypography
                          variant={WsTypoVariants.BODY1}
                          color={data.value === 'NA' ? WsTypoColors.GREYTEXT : WsTypoColors.BLACK}
                        >
                          {data.value}
                        </WsTypography>
                      )}
                    </Grid>
                  </Grid>
                ))}
                {jobInfo.tags && (
                  <Grid container xs={6} p={2} alignItems="center">
                    <Grid item xs={1}>
                      <StyleIcon sx={{ color: WsColors.GreyBlack }} />
                    </Grid>
                    <Grid item xs={3}>
                      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.TERTIARY900}>
                        Tags:
                      </WsTypography>
                    </Grid>
                    <Grid item xs={8}>
                      <WsTagsList
                        chipArray={jobInfo.tags}
                        count={3}
                        type={ChipsType.PRIMARY}
                        borderColorFlag={true}
                      />
                    </Grid>
                  </Grid>
                )}
                {jobInfo.jobDescription && (
                  <Grid container pb={2} pt={1} alignItems="center">
                    <Grid item pt={0.5}>
                      <DescriptionIcon sx={{ color: WsColors.GreyBlack }} />
                    </Grid>
                    <Grid item pl={1}>
                      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.TERTIARY900}>
                        Description:
                      </WsTypography>
                    </Grid>
                    <Grid item xs={10} pl={2}>
                      <Typography
                        dangerouslySetInnerHTML={{
                          __html: jobInfo.jobDescription
                            ? jobInfo.jobDescription.replace(
                                /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
                                'illegalscript'
                              )
                            : '',
                        }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Popover>
        </div>
      </ClickAwayListener>
    </Grid>
  );
};

export default CrmJobDialog;
